import { useState } from "react";
import { toast } from "sonner";
import { Search, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { BRAND } from "../lib/brand";

export default function Sourcing() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    item_name: "",
    item_link: "",
    quantity: 1,
    budget_usd: "",
    delivery_country: "Jamaica",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity) || 1,
        budget_usd: form.budget_usd ? parseFloat(form.budget_usd) : null,
      };
      await api.post("/sourcing-requests", payload);
      setDone(true);
      toast.success("Sourcing request submitted!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#F1F5F9]" data-testid="sourcing-success">
        <div className="max-w-md text-center bg-white p-10 border border-slate-200 rounded-sm">
          <CheckCircle2 className="h-14 w-14 text-[#1E2A78] mx-auto mb-4" />
          <h2 className="text-2xl font-display font-bold text-[#0F172A]">Request received</h2>
          <p className="mt-2 text-slate-600">Our sourcing team will email you with options and pricing within 24–48 hours.</p>
          <button onClick={() => { setDone(false); setForm({ ...form, item_name: "", item_link: "", notes: "" }); }} className="mt-6 px-5 py-2 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200]">
            Submit another
          </button>
        </div>
      </div>
    );
  }

  const input = "w-full px-4 py-3 bg-white border border-slate-200 rounded-sm outline-none focus:border-[#1E2A78] focus:ring-2 focus:ring-[#1E2A78]/20 text-sm";
  const label = "block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2";

  return (
    <div data-testid="sourcing-page">
      <section className="bg-[#1E2A78] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 text-[#FFD700] mb-3">
            <Search className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Sourcing</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight max-w-3xl">
            Can't find it locally? <span className="text-[#FFD700]">We'll source it for you.</span>
          </h1>
          <p className="mt-6 text-slate-200 max-w-2xl">
            Tell us what you need — a brand, a part, a product link — and our team will source it in Canada/USA, consolidate with your shipment and deliver to your door in the Caribbean.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={submit} className="space-y-5 bg-white border border-slate-200 p-8 rounded-sm" data-testid="sourcing-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={label}>Full Name</label>
                <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={input} data-testid="sourcing-name" />
              </div>
              <div>
                <label className={label}>Phone</label>
                <input required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={input} data-testid="sourcing-phone" />
              </div>
            </div>
            <div>
              <label className={label}>Email</label>
              <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={input} data-testid="sourcing-email" />
            </div>
            <div>
              <label className={label}>Item / Product Name</label>
              <input required value={form.item_name} onChange={(e) => set("item_name", e.target.value)} className={input} data-testid="sourcing-item" />
            </div>
            <div>
              <label className={label}>Product Link (optional)</label>
              <input value={form.item_link} onChange={(e) => set("item_link", e.target.value)} placeholder="https://..." className={input} data-testid="sourcing-link" />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className={label}>Quantity</label>
                <input required type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className={input} data-testid="sourcing-qty" />
              </div>
              <div>
                <label className={label}>Budget (USD, optional)</label>
                <input type="number" min="0" step="0.01" value={form.budget_usd} onChange={(e) => set("budget_usd", e.target.value)} className={input} data-testid="sourcing-budget" />
              </div>
              <div>
                <label className={label}>Delivery Country</label>
                <select value={form.delivery_country} onChange={(e) => set("delivery_country", e.target.value)} className={input} data-testid="sourcing-country">
                  {BRAND.destinations.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={label}>Additional Notes</label>
              <textarea rows="4" value={form.notes} onChange={(e) => set("notes", e.target.value)} className={input} placeholder="Color, size, brand preferences, deadlines..." data-testid="sourcing-notes" />
            </div>
            <button type="submit" disabled={submitting} className="w-full px-6 py-3 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200] disabled:opacity-60" data-testid="sourcing-submit">
              {submitting ? "Submitting..." : "Submit Sourcing Request"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
